import React, { useState, useEffect } from "react";
import createAxiosInstance from "../../utils/axiosConfig";
import { register, register_customer } from "../../utils/endpoints";
import { validate } from "../../utils/formValidate";
import Alert from "../common/alerts/alert";
import { useAuth } from "../../context/authContext";

interface RegisterProps {
	isForServiceProvider?: boolean;
	onSuccess?: () => void;
	isUpdateMode?: boolean;
	userData?: any;
	updateEndpoint?: string;
}

const Register: React.FC<RegisterProps> = ({ 
	isForServiceProvider, 
	onSuccess, 
	isUpdateMode = false, 
	userData: initialUserData,
	updateEndpoint 
}) => {
	const {isAuthenticated, userData} = useAuth();
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		password: "",
		confirmPassword: "",
		company: "",
		title: "",
		industry: "Agriculture, Forestry, Fishing and Hunting",
		email: "",
		companySize: "0 - 500",
		budgetAccountability: "0 - $1M",
	});

	const [errors, setErrors] = useState<any>({});
	const [responseError, setResponseError] = useState<any>("");
	const [alertVisible, setAlertVisible] = useState(false);
	const [loading, setLoading] = useState(false);

	// Pre-populate form data when in update mode
	useEffect(() => {
		if (isUpdateMode && initialUserData) {
			// Handle name splitting if we have full_name instead of separate first/last names
			let firstName = initialUserData.first_name || "";
			let lastName = initialUserData.last_name || "";
			
			// If we don't have separate first/last names but have a full name, split it
			if (!firstName && !lastName && initialUserData.full_name) {
				const nameParts = initialUserData.full_name.trim().split(' ');
				firstName = nameParts[0] || "";
				lastName = nameParts.slice(1).join(' ') || ""; // Join remaining parts as last name
			} else if (!firstName && !lastName && initialUserData.name) {
				// Handle case where the field might be called 'name'
				const nameParts = initialUserData.name.trim().split(' ');
				firstName = nameParts[0] || "";
				lastName = nameParts.slice(1).join(' ') || "";
			}

			setFormData({
				firstName: firstName,
				lastName: lastName,
				password: "",
				confirmPassword: "",
				company: initialUserData.company || "",
				title: initialUserData.title || "",
				industry: initialUserData.industry || "Agriculture, Forestry, Fishing and Hunting",
				email: initialUserData.email || "",
				companySize: initialUserData.company_size || "0 - 500",
				budgetAccountability: initialUserData.budget_accountability || "0 - $1M",
			});
		}
	}, [isUpdateMode, initialUserData]);

	const handleChange = (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
		>
	) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
		validate({ [name]: value }, setErrors);
		if (name === "confirmPassword" && !isUpdateMode) {
			if (value !== formData.password) {
				setErrors({ ...errors, confirmPassword: "Passwords do not match." });
			} else {
				setErrors({ ...errors, confirmPassword: "" });
			}
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		// Create validation object - exclude password fields for update mode
		let validationData: any = { ...formData };
		if (isUpdateMode) {
			const { password, confirmPassword, ...rest } = validationData;
			validationData = rest;
		}
		
		if (!validate(validationData, setErrors)) return;

		setLoading(true);

		const payload: any = {
			first_name: formData.firstName,
			last_name: formData.lastName,
			company: formData.company,
			title: formData.title,
			industry: formData.industry,
			email: formData.email,
			company_size: formData.companySize,
			budget_accountability: formData.budgetAccountability,
		};

		// Add password only for registration (not update)
		if (!isUpdateMode) {
			payload.password = formData.password;
		}

		// Add user_id for update mode
		if (isUpdateMode && initialUserData?.id) {
			payload.user_id = initialUserData.id;
		}

		try {
			const axiosInstance = createAxiosInstance();
			
			// Determine endpoint and method
			let endpoint;
			let method = 'post';
			
			if (isUpdateMode && updateEndpoint) {
				endpoint = updateEndpoint;
				method = 'put';
			} else {
				endpoint = isForServiceProvider ? register_customer : register;
			}

			const response = method === 'put' 
				? await axiosInstance.put(endpoint, payload)
				: await axiosInstance.post(endpoint, payload);

			if(response.status === 404 || response.status === 402){
				setResponseError("Session expired. logging out...");
				setAlertVisible(true);
				setTimeout(() => {
					window.location.href = "/login";
				}, 1000);
			}

			if (response.data?.user?.error) {
				setResponseError(response?.data?.user?.error);
				setAlertVisible(true);
			} else {
				setAlertVisible(true);
				if (!isUpdateMode) {
					// Only clear form for new registrations
					setFormData({
						firstName: "",
						lastName: "",
						password: "",
						confirmPassword: "",
						company: "",
						title: "",
						industry: "Agriculture, Forestry, Fishing and Hunting",
						email: "",
						companySize: "0 - 500",
						budgetAccountability: "0 - $1M",
					});
				}
				// Call onSuccess callback if operation was successful
				if (onSuccess) {
					// Add a small delay to let the success alert show briefly
					setTimeout(() => {
						onSuccess();
					}, 1500);
				}
			}
		} catch (err) {
			setResponseError("Some errors occured. Please try again later.");
			setAlertVisible(true);
		} finally {
			setLoading(false);
		}
	};

	return (
		<section className="">
			<div className="mt-5" style={{
				paddingBottom: '5%',
			}}>
				<div className="col-md-12 col-lg-2 col-xl-12">
					<div className="card  mx-auto">
						<div className="card-body">
							<form action="" onSubmit={handleSubmit}>
								<div className="row">
									<div className="col">
										<div className="mb-3">
											<label htmlFor="firstname" className="form-label">First Name</label>
											<input
												type="text"
												className={`form-control bg-white ${
													errors.firstName ? "is-invalid" : ""
												}`}
												id="firstname"
												name="firstName"
												placeholder=""
												value={formData.firstName}
												onChange={handleChange}
											/>
											
											{errors.firstName && (
												<div className="text-danger small">
													{errors.firstName}
												</div>
											)}
										</div>
									</div>
									<div className="col">
										<div className="mb-3">
											<label htmlFor="lastname" className="form-label">Last Name</label>
											<input
												type="text"
												className={`form-control bg-white ${
													errors.lastName ? "is-invalid" : ""
												}`}
												id="lastname"
												name="lastName"
												placeholder=""
												value={formData.lastName}
												onChange={handleChange}
											/>
											
											{errors.lastName && (
												<div className="text-danger small">
													{errors.lastName}
												</div>
											)}
										</div>
									</div>
								</div>
								
								{/* Only show password fields when not in update mode */}
								{!isUpdateMode && (
									<div className="row mb-3">
										<div className="col">
											<label htmlFor="password" className="form-label">Password</label>
											<input
												type="password"
												className={`form-control bg-white ${
													errors.password ? "is-invalid" : ""
												}`}
												id="password"
												name="password"
												value={formData.password}
												onChange={handleChange}
											/>
											
											{errors.password && (
												<div className="text-danger small">{errors.password}</div>
											)}
										</div>
										<div className="col">
											<label htmlFor="confirm-password" className="form-label">Confirm Password</label>
											<input
												type="password"
												className={`form-control bg-white ${
													errors.confirmPassword ? "is-invalid" : ""
												}`}
												id="confirm-password"
												name="confirmPassword"
												value={formData.confirmPassword}
												onChange={handleChange}
											/>
											{errors.confirmPassword && (
												<div className="text-danger small">
													{errors.confirmPassword}
												</div>
											)}
										</div>
										<div className="form-text text-start">
											<i className="bi bi-info-circle"></i> Use 8 or more
											characters with a mix of letters, numbers & symbols.
										</div>
									</div>
								)}
								
								<div className="row mb-3 ">
									<div className="col">
										<div className="mb-3">
											<label htmlFor="company" className="form-label">Company</label>
											<input
												type="text"
												className={`form-control bg-white ${
													errors.company ? "is-invalid" : ""
												}`}
												id="company"
												name="company"
												placeholder=""
												value={formData.company}
												onChange={handleChange}
											/>
											{errors.company && (
												<div className="text-danger small">
													{errors.company}
												</div>
											)}
										</div>
									</div>
									<div className="col">
										<div className="mb-3">
											<label htmlFor="title" className="form-label">Title</label>
											<input
												type="text"
												className={`form-control bg-white ${
													errors.title ? "is-invalid" : ""
												}`}
												id="title"
												name="title"
												placeholder=""
												value={formData.title}
												onChange={handleChange}
											/>
											{errors.title && (
												<div className="text-danger small">{errors.title}</div>
											)}
										</div>
									</div>
								</div>

								<div className="mb-3">
									<label htmlFor="floatingSelect" className="form-label">Industry</label>
									<select
										className={`form-select bg-white ${
											errors.industry ? "is-invalid" : ""
										}`}
										id="floatingSelect"
										aria-label="Floating label select example"
										name="industry"
										value={formData.industry}
										onChange={handleChange}
									>
										<option value="Agriculture, Forestry, Fishing and Hunting">
											Agriculture, Forestry, Fishing and Hunting
										</option>
										<option value="Mining, Quarrying, and Oil and Gas Extraction">
											Mining, Quarrying, and Oil and Gas Extraction
										</option>
										<option value="Utilities">Utilities</option>
										<option value="Construction">Construction</option>
										<option value="Manufacturing">Manufacturing</option>
										<option value="Wholesale Trade">Wholesale Trade</option>
										<option value="Retail Trade">Retail Trade</option>
										<option value="Transportation and Warehousing">
											Transportation and Warehousing
										</option>
										<option value="Information">Information</option>
										<option value="Finance and Insurance">
											Finance and Insurance
										</option>
										<option value="Real Estate and Rental and Leasing">
											Real Estate and Rental and Leasing
										</option>
										<option value="Professional, Scientific, and Technical Services">
											Professional, Scientific, and Technical Services
										</option>
										<option value="Management of Companies and Enterprises">
											Management of Companies and Enterprises
										</option>
										<option value="Administrative and Support and Waste Management and Remediation Services">
											Administrative and Support and Waste Management and
											Remediation Services
										</option>
										<option value="Educational Services">
											Educational Services
										</option>
										<option value="Health Care and Social Assistance">
											Health Care and Social Assistance
										</option>
										<option value="Arts, Entertainment, and Recreation">
											Arts, Entertainment, and Recreation
										</option>
										<option value="Accommodation and Food Services">
											Accommodation and Food Services
										</option>
										<option value="Other Services (except Public Administration)">
											Other Services (except Public Administration)
										</option>
										<option value="Public Administration">
											Public Administration
										</option>
									</select>

									{errors.industry && (
										<div className="text-danger small">{errors.industry}</div>
									)}
								</div>

								<div className="mb-3">
									<label htmlFor="email" className="form-label">Email</label>
									<input
										type="email"
										className={`form-control bg-white ${
											errors.email ? "is-invalid" : ""
										}`}
										style={{
											cursor: isUpdateMode ? 'not-allowed' : 'auto',
											backgroundColor: isUpdateMode ? '#f0f0f0' : 'white',
											opacity: isUpdateMode ? 0.5 : 1,
										}}
										id="email"
										name="email"
										disabled={isUpdateMode}
										value={formData.email}
										onChange={handleChange}
									/>
									{errors.email && (
										<div className="text-danger small">{errors.email}</div>
									)}
								</div>

								<div className="row mb-3">
									<div className="col">
										<div className="mb-3">
											<label htmlFor="companySize" className="form-label">Company Size</label>
											<select
												className={`form-select bg-white ${
													errors.companySize ? "is-invalid" : ""
												}`}
												id="floatingSelect"
												aria-label="Floating label select example"
												name="companySize"
												value={formData.companySize}
												onChange={handleChange}
											>
												<option selected value="0 - 500">
													0 - 500
												</option>
												<option value="500 - 5,000">500 - 5,000</option>
												<option value="5,000 – 10,000">5,000 – 10,000</option>
												<option value="10,000 – 30,000">10,000 – 30,000</option>
												<option value="30,000+">30,000+ </option>
											</select>
											{errors.companySize && (
												<div className="text-danger small">
													{errors.companySize}
												</div>
											)}
										</div>
									</div>
									<div className="col">
										<div className="mb-3">
											<label htmlFor="budgetAccountability" className="form-label">Budget Accountability</label>
											<select
												className={`form-select bg-white ${
													errors.budgetAccountability ? "is-invalid" : ""
												}`}
												id="floatingSelect"
												aria-label="Floating label select example"
												name="budgetAccountability"
												value={formData.budgetAccountability}
												onChange={handleChange}
											>
												<option selected value="0 - $1M">
													0 - $1M{" "}
												</option>
												<option value="$1M – $30M">$1M – $30M</option>
												<option value="$30M - $60M">$30M - $60M</option>
												<option value="$60M - $90M">$60M - $90M</option>
												<option value="$90M - $150M">$90M - $150M</option>
												<option value="$60M - $90M">$60M - $90M</option>
												<option value="$150M +">$150M +</option>
											</select>
											{errors.budgetAccountability && (
												<div className="text-danger small">
													{errors.budgetAccountability}
												</div>
											)}
										</div>
									</div>
								</div>

								<div className="mb-3 d-flex justify-content-end">
									<button
										type="submit"
										className="btn btn-rounded-blue w-30"
										disabled={Object.keys(errors).length > 0 || loading}
										style={{
											background: '#26397E',
											borderRadius: "77.125rem",
											transition: 'all 0.3s ease',
										}}
										onMouseEnter={(e) => {
											const target = e.target as HTMLButtonElement;
											target.style.backgroundColor = '#5376F0 !important';
											target.style.transform = 'translateY(-2px)';
										}}
										onMouseLeave={(e) => {
											const target = e.target as HTMLButtonElement;
											target.style.backgroundColor = '#26397E';
											target.style.transform = 'translateY(0)';
										}}
									>
										{loading ? (
											<div
												className="d-flex justify-content-center align-items-center"
												style={{ height: "20px", color: 'white' }}
											>
												<div className="spinner-border text-white" role="status">
													<span className="visually-hidden">Loading...</span>
												</div>
											</div>
										) : (
											isUpdateMode ? 'UPDATE USER' : (userData?.is_admin ? 'SAVE' : 'SAVE USER')
										)}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>
			<Alert
				message={`${
					responseError.length > 1
						? responseError
						: isUpdateMode ? "User updated successfully." : "User registration successful."
				}`}
				type={`${responseError.length > 1 ? "danger" : "success"}`}
				show={alertVisible}
				onClose={() => setAlertVisible(false)}
			/>
		</section>
	);
};

export default Register;
